import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import {
  Search, FileText, AlertTriangle, CheckSquare, Package,
  Paperclip, Shield, Mail, ArrowRight, Command, Loader2
} from 'lucide-react';

const API = '/api';

const CATEGORY_CONFIG = {
  investigations: { icon: Search, label: 'Investigations', color: 'text-blue-500', bg: 'bg-blue-50' },
  incidents: { icon: AlertTriangle, label: 'Incidents', color: 'text-amber-500', bg: 'bg-amber-50' },
  tasks: { icon: CheckSquare, label: 'Tasks', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  inventory: { icon: Package, label: 'Equipment', color: 'text-purple-500', bg: 'bg-purple-50' },
  evidence: { icon: Paperclip, label: 'Evidence', color: 'text-cyan-500', bg: 'bg-cyan-50' },
  recommendations: { icon: Shield, label: 'Recommendations', color: 'text-red-500', bg: 'bg-red-50' },
  correspondence: { icon: Mail, label: 'Correspondence', color: 'text-indigo-500', bg: 'bg-indigo-50' },
};

function ResultItem({ item, category, onClick }) {
  const config = CATEGORY_CONFIG[category];
  const Icon = config.icon;

  const getTitle = () => {
    switch (category) {
      case 'investigations': return item.investigation_number;
      case 'incidents': return item.incident_number;
      case 'tasks': return item.title;
      case 'inventory': return item.name;
      case 'evidence': return item.file_name;
      case 'recommendations': return item.rec_number;
      case 'correspondence': return item.ref_number;
      default: return '';
    }
  };

  const getSubtext = () => {
    switch (category) {
      case 'investigations': return `${item.title} | ${item.sector?.toUpperCase()} | ${item.status}`;
      case 'incidents': return `${item.location || ''} | ${item.severity} | ${item.identifier || ''}`;
      case 'tasks': return `${item.investigation_number} | ${item.assigned_to_name || 'Unassigned'} | ${item.status}`;
      case 'inventory': return `${item.asset_tag} | ${item.serial_number || ''} | ${item.status}`;
      case 'evidence': return `${item.investigation_number || ''} | ${item.file_category || ''}`;
      case 'recommendations': return `${item.addressee} | ${item.classification}`;
      case 'correspondence': return `${item.subject} | ${item.type}`;
      default: return '';
    }
  };

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left group"
      data-testid={`search-result-${category}-${item.id}`}
    >
      <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center shrink-0`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{getTitle()}</div>
        <div className="text-xs text-slate-500 truncate">{getSubtext()}</div>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  );
}

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
      setResults(null);
      setSelectedIndex(0);
    }
  }, [open]);

  // Debounced search
  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/search`, { params: { q }, withCredentials: true });
      setResults(data);
      setSelectedIndex(0);
    } catch { setResults(null); }
    finally { setLoading(false); }
  }, []);

  const handleInputChange = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length >= 2) {
      setLoading(true);
      debounceRef.current = setTimeout(() => doSearch(val), 300);
    } else {
      setResults(null);
      setLoading(false);
    }
  };

  // Navigate to result
  const handleSelect = (category, item) => {
    setOpen(false);
    switch (category) {
      case 'investigations':
        navigate(`/investigations/${item.id}`);
        break;
      case 'incidents':
        navigate('/incidents');
        break;
      case 'tasks':
        if (item.investigation_id) navigate(`/investigations/${item.investigation_id}`);
        else navigate('/tasks');
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      case 'evidence':
        if (item.investigation_id) navigate(`/investigations/${item.investigation_id}`);
        else navigate('/evidence');
        break;
      case 'recommendations':
        navigate('/safety-recommendations');
        break;
      case 'correspondence':
        navigate('/correspondence');
        break;
      default:
        break;
    }
  };

  // Build flat list for keyboard nav
  const flatResults = [];
  if (results) {
    for (const category of Object.keys(CATEGORY_CONFIG)) {
      for (const item of (results[category] || [])) {
        flatResults.push({ category, item });
      }
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
      e.preventDefault();
      const { category, item } = flatResults[selectedIndex];
      handleSelect(category, item);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const hasResults = results && results.total > 0;
  const noResults = results && results.total === 0 && query.length >= 2;

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/5 border border-slate-600 hover:border-slate-500 hover:bg-white/10 transition-all text-slate-400 cursor-pointer"
        data-testid="global-search-trigger"
      >
        <Search className="w-4 h-4 shrink-0" />
        <span className="text-sm flex-1 text-left">Search everything...</span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono font-medium bg-slate-700 text-slate-400 rounded border border-slate-600">
          <Command className="w-2.5 h-2.5" />K
        </kbd>
      </button>

      {/* Search Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl p-0 gap-0 bg-white border-slate-200 shadow-2xl overflow-hidden sm:rounded-xl" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Global Search</DialogTitle>
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
            <Search className="w-5 h-5 text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search investigations, incidents, tasks, equipment..."
              className="flex-1 text-sm text-slate-900 placeholder:text-slate-400 outline-none bg-transparent"
              data-testid="global-search-input"
            />
            {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin shrink-0" />}
            <kbd className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">ESC</kbd>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
            {/* Empty state */}
            {!results && !loading && query.length < 2 && (
              <div className="px-4 py-8 text-center">
                <Search className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Type at least 2 characters to search</p>
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {['AVN-INV', 'runway', 'Boeing', 'Drone', 'collision'].map(term => (
                    <button key={term} onClick={() => handleInputChange(term)}
                      className="px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded-full hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      data-testid={`search-suggestion-${term}`}>
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No results */}
            {noResults && (
              <div className="px-4 py-8 text-center" data-testid="search-no-results">
                <p className="text-sm text-slate-500">No results found for "<span className="font-medium text-slate-700">{query}</span>"</p>
                <p className="text-xs text-slate-400 mt-1">Try different keywords or check spelling</p>
              </div>
            )}

            {/* Result groups */}
            {hasResults && (() => {
              let flatIdx = 0;
              return Object.entries(CATEGORY_CONFIG).map(([category, config]) => {
                const items = results[category] || [];
                if (items.length === 0) return null;
                const Icon = config.icon;
                return (
                  <div key={category} className="px-2 py-1.5" data-testid={`search-group-${category}`}>
                    <div className="flex items-center gap-2 px-2 py-1.5">
                      <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{config.label}</span>
                      <span className="text-[10px] text-slate-300 ml-auto">{items.length}</span>
                    </div>
                    {items.map((item) => {
                      const thisIdx = flatIdx++;
                      return (
                        <div key={item.id} className={thisIdx === selectedIndex ? 'bg-blue-50 rounded-lg' : ''}>
                          <ResultItem item={item} category={category} onClick={() => handleSelect(category, item)} />
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>

          {/* Footer */}
          {hasResults && (
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <span className="text-[11px] text-slate-400">{results.total} result{results.total !== 1 ? 's' : ''} found</span>
              <div className="flex items-center gap-3 text-[11px] text-slate-400">
                <span><kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono mr-0.5">&#8593;&#8595;</kbd> navigate</span>
                <span><kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono mr-0.5">&#8629;</kbd> open</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
